import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-mui";
import * as Yup from "yup";
import { Typography, Button, Box } from "@mui/material";
import Modal from "../Modal/Modal";
import HTMLEditor from "../HtmlEditor/HtmlEditor";
import usePolicyActivities from "../../hooks/policyActivities";
import { useSnackbarNotifications } from "../../hooks/snackbar";

const getAcknowledgementSchema = t => {
  return Yup.object().shape({
    name: Yup.string()
      .required(t("validationMessages.titleRequired"))
      .max(
        150,
        t("validationMessages.cannotExceedLength", {
          type: "Title",
          maxLength: 150
        })
      ),
    textBody: Yup.string().required(
      t("validationMessages.acknowledgementTextRequired")
    ),
    textBodyTextOnlyLength: Yup.number().max(
      600,
      t("validationMessages.cannotExceedLength", {
        type: t("policyBuilder.acknowledgementModalFields.text"),
        maxLength: 600
      })
    )
  });
};

const AcknowledgementModalForm = ({
  submitForm,
  values,
  errors,
  touched,
  setTouched,
  onClose,
  open,
  isSubmitting,
  editExisting,
  setSubmitting,
  resetForm,
  setFieldValue,
  acknowledgementData,
  isRequesting
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    resetForm();
  }, [open]);

  // Once the form is submitted and isRequesting becomes false, re-enable the form
  useEffect(() => {
    if (isSubmitting && !isRequesting) {
      setSubmitting(false);
    }
  }, [isRequesting, isSubmitting]);

  const handleNumCharactersChange = numCharacters => {
    setFieldValue("textBodyTextOnlyLength", numCharacters);
  };

  const dialogTitle = t(
    `policyBuilder.${editExisting ? "edit" : "create"}AcknowledgementModalTitle`
  );

  return (
    <Form data-qa="acknowledgement-modal-form">
      <Modal
        scrollable
        disableEnforceFocus
        showCloseIcon
        maxWidth="lg"
        fullWidth
        dialogTitle={
          <Typography variant="h4" color="textPrimary">
            {dialogTitle}
          </Typography>
        }
        dialogContent={
          <Box display="flex" flexDirection="column" mb={1}>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              mb={1.5}
              mt={1}
            >
              <Box flex={1} mb={3}>
                <Field
                  variant="filled"
                  fullWidth
                  label={t("policyBuilder.acknowledgementModalFields.title")}
                  component={TextField}
                  name="name"
                  inputProps={{ "data-qa": "acknowledgement-form-title-field" }}
                />
              </Box>
            </Box>
            <Box mb={-2}>
              <Typography variant="subtitle1" color="textSecondary">
                {t("policyBuilder.acknowledgementModalFields.text")}
              </Typography>
            </Box>
            <HTMLEditor
              height={189}
              disabled={isSubmitting || isRequesting}
              onChange={value => {
                setFieldValue("textBody", value, false);
              }}
              onBlur={() => setTouched({ ...touched, textBody: true })}
              value={values.textBody}
              menubar={false}
              plugins={["lists", "link", "image", "media", "code"]}
              toolbar="undo redo | bold italic underline | alignment numlist bullist outdent indent | link"
              errorMessage={touched.textBody && errors.textBody}
              characterLimit={600}
              onNumCharactersChange={handleNumCharactersChange}
            />
          </Box>
        }
        dialogActions={
          <>
            <Button
              color="primary"
              disabled={isSubmitting || isRequesting}
              onClick={() => onClose()}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={submitForm}
              disabled={isSubmitting || isRequesting}
            >
              {t("save")}
            </Button>
          </>
        }
        open={open}
        onClose={() => onClose()}
      />
    </Form>
  );
};

const AcknowledgementModal = ({
  onClose,
  open,
  acknowledgementId,
  shortCourseId
}) => {
  const { t } = useTranslation();
  const {
    fetchPolicyAcknowledgement,
    createPolicyAcknowledgement,
    updatePolicyAcknowledgement,
    isRequesting
  } = usePolicyActivities();
  const [acknowledgementData, setAcknowledgementData] = useState(null);

  const { displayErrorMessage } = useSnackbarNotifications();

  useEffect(() => {
    if (acknowledgementId) {
      fetchPolicyAcknowledgement(acknowledgementId).then(result =>
        setAcknowledgementData(result)
      );
    }
  }, [open]);

  const handleFormSubmit = values => {
    if (acknowledgementId) {
      updatePolicyAcknowledgement(acknowledgementId, values)
        .then(() => onClose(true))
        .catch(error => {
          console.log(error);
          displayErrorMessage(t("policyBuilder.updateAcknowledgementError"));
        });
    } else {
      createPolicyAcknowledgement(values)
        .then(() => onClose(true))
        .catch(error => {
          console.log(error);
          displayErrorMessage(t("policyBuilder.createAcknowledgementError"));
        });
    }
  };

  return (
    <Formik
      initialValues={{
        name: acknowledgementData ? acknowledgementData.name : "",
        textBody: acknowledgementData ? acknowledgementData.textBody : "",
        courseSectionId: shortCourseId,
        textBodyTextOnlyLength: 0
      }}
      validationSchema={getAcknowledgementSchema(t)}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        delete values.textBodyTextOnlyLength;
        handleFormSubmit(values);
      }}
    >
      {({
        submitForm,
        isSubmitting,
        setSubmitting,
        values,
        resetForm,
        setFieldValue,
        errors,
        touched,
        setTouched
      }) => (
        <AcknowledgementModalForm
          setSubmitting={setSubmitting}
          submitForm={submitForm}
          isSubmitting={isSubmitting}
          values={values}
          editExisting={acknowledgementId > 0}
          resetForm={resetForm}
          open={open}
          onClose={onClose}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          acknowledgementData={acknowledgementData}
          isRequesting={isRequesting}
          setTouched={setTouched}
        />
      )}
    </Formik>
  );
};

AcknowledgementModal.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  shortCourseId: PropTypes.number,
  acknowledgementId: PropTypes.number
};

export default AcknowledgementModal;
